import { z } from 'zod';
import { cognitoUserSchema } from './UserInfo.schema';

const CommsRoutingSchema = z.object({
  comms_routing: z.string(),
  comms_routing_plan_id: z.string().optional(),
  comms_target: z.string().optional(),
  count: z.string(),
});

const SummaryReportCommsRoutingSchema = z.object({
  comms_routing: z.array(CommsRoutingSchema),
});

const ExclusionsSchema = z.object({
  rule_names: z.string(),
  age_max: z.string(),
  priority: z.string(),
  type: z.string(),
  exclusions_count: z.string(),
  age_min: z.string(),
});

const SummaryReportExclusionsSchema = z.object({
  exclusions: z.array(ExclusionsSchema),
});

const TotalsSchema = z.object({
  invited: z.number(),
  filtered: z.number(),
  suppressed: z.number(),
});

const SummaryReportTotalsSchema = z.object({
  totals: TotalsSchema,
});

const CountsSchema = z.object({
  distinct_comms_routing: z.string(),
  excluded_count: z.string(),
  cohort_label: z.string(),
  priority: z.string(),
  invited_count: z.string(),
  invited_other_cohort: z.string(),
  min_age: z.string(),
  max_age: z.string(),
  cohort_label_count: z.string(),
  filtered_count: z.string(),
  suppressed_count: z.string(),
});

const SummaryReportCountsSchema = z.object({
  counts: z.array(CountsSchema),
});

const IcbSplitSchema = z.object({
  icb: z.string(),
  count: z.string(),
});

const SummaryReportIcbSplitSchema = z.object({
  icb_split: z.array(IcbSplitSchema),
});

export const dynamoCognitoUserSchema = z.strictObject({
  SUB: z.string(),
  USERNAME: z.string(),
  EMAIL: z.string(),
  GIVEN_NAME: z.string(),
  FAMILY_NAME: z.string(),
});
export type DynamoCognitoUser = z.infer<typeof dynamoCognitoUserSchema>;

export enum SUMMARY_REPORT_REVIEW_STATUSES {
  report_ready='report ready',
  report_approved='report approved',
  report_rejected='report rejected',
  sent_to_nhs_notify='sent to nhs notify',
}

export const SummaryReportReviewSchema = z
  .strictObject({
    status: z.nativeEnum(SUMMARY_REPORT_REVIEW_STATUSES),
    reviewer: cognitoUserSchema,
    timestamp: z.number(),
  })
  .optional();

// See
// https://github.com/typescript-eslint/typescript-eslint/issues/10266
// https://typescript-eslint.io/rules/no-unused-vars/#why-does-this-rule-report-variables-used-only-for-types
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SummaryReportSchema = z.object({
  summary_report_review: SummaryReportReviewSchema,
  summary_report_comms_routing: SummaryReportCommsRoutingSchema,
  summary_report_exclusions: SummaryReportExclusionsSchema,
  summary_report_totals: SummaryReportTotalsSchema,
  summary_report_counts: SummaryReportCountsSchema,
  summary_report_icb_split: SummaryReportIcbSplitSchema,
  campaign_id: z.string(),
  campaign_version: z.number().optional(),
  iteration_id: z.string(),
  iteration_version: z.number().optional(),
  batch_id: z.string(),
  run_time: z.string().optional(),
  dry_run: z.boolean(),
  reviewers: z.array(z.string()),
});

export type SummaryReport = z.infer<typeof SummaryReportSchema>;

export const summaryReportReviewPATCHSchema = z.strictObject({
  SUMMARY_REPORT_REVIEW: z
    .strictObject({
      STATUS: z
        .nativeEnum(SUMMARY_REPORT_REVIEW_STATUSES)
        .refine(
          (value) => value !== SUMMARY_REPORT_REVIEW_STATUSES.sent_to_nhs_notify,
          {
            message: 'The PUT summary-report/submit endpoint must be used to update the summary report status.',
          },
        ),
      REVIEWER: dynamoCognitoUserSchema.optional(),
      TIMESTAMP: z.number().optional(),
    })
    .optional(),
});
export type SummaryReportReviewPATCH = z.infer<typeof summaryReportReviewPATCHSchema>;

export const summaryReportSelectedReviewersPATCHSchema = z.strictObject({
  REVIEWERS: z.array(z.string()),
});
export type SummaryReportSelectedReviewersPATCH = z.infer<typeof summaryReportSelectedReviewersPATCHSchema>;
